// 主線程：將 Worker snapshot 還原到 reactive actorManager / dcManager
import { CustomReactive } from "@/lib/util";
import { ActorManager, EntityActor, GroupActor } from "@/eventActor";
import { DamageCollectorManager } from "@/actionCollector";
import type { WorkerSnapshot } from "./workerProtocol";

export function hydrateFromSnapshot(
    snapshot: WorkerSnapshot,
    actorMgr: ActorManager,
    dcMgr: DamageCollectorManager,
): void {
    // 1. 暫停 Vue reactivity，避免每次賦值都觸發 re-render
    actorMgr.pauseReactivity();

    try {
        // 2. 清除舊的 map（保留 shallowReactive 物件本身）
        for (const k in actorMgr.entityMap) delete actorMgr.entityMap[k];
        for (const k in actorMgr.groupMap) delete actorMgr.groupMap[k];
        actorMgr.damages.length = 0;

        // 3. 建立 GroupActor 實例
        for (const [key, sg] of Object.entries(snapshot.groups)) {
            const group = CustomReactive(
                new GroupActor(actorMgr, key, sg.raceId, sg.name),
            );
            group.hydrateFrom(sg);
            actorMgr.groupMap[key] = group;
        }

        // 4. 建立 EntityActor 實例
        for (const [id, se] of Object.entries(snapshot.entities)) {
            const group = actorMgr.groupMap[se.groupKey];
            if (!group) {
                console.warn(
                    `hydrateFromSnapshot: group not found for entity ${id}, groupKey=${se.groupKey}`,
                );
                continue;
            }

            const entity = CustomReactive(
                new EntityActor(actorMgr, id, se.raceId, se.name, group),
            );
            entity.hydrateFrom(se);
            actorMgr.entityMap[id] = entity;
            // group.entityMap 的 getter 回傳的是 _entityMap 的參照，直接賦值即可
            group.entityMap[id] = entity;
        }

        // 5. 還原 raw damage log（避免 spread 大陣列造成 call stack overflow）
        for (const d of snapshot.damages) actorMgr.damages.push(d);
    } finally {
        // 6. 恢復 reactivity，觸發一次性 re-render
        actorMgr.resumeReactivity();
    }

    // 7. 重播 dcManager damages（通知所有已訂閱的 collector）
    //    在 resumeReactivity 之後執行，確保 entities 已就位
    dcMgr.clear();
    for (const d of snapshot.collectorDamages) {
        dcMgr.onDamage(d as any);
    }
}
